"use client"

import { type QueryClient, useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef } from "react"

import {
  getMyCreditRegistrationEnrolmentBannersQueryKey,
  getMyCreditRegistrationsQueryKey,
} from "@/generated/api/@tanstack/react-query.generated"
import {
  dismissCreditRegistrationEnrolmentBanner,
  recordMyEnrolmentPageVisit,
  requestCreditRegistrationEnrolmentRecheck,
} from "@/generated/api/sdk.generated"
import type { MyCreditRegistration } from "@/generated/api/types.generated"
import useToastMutation from "@/shared-module/common/hooks/useToastMutation"

// The by-course-module cache is keyed per module and the enrolment is a course-wide fact, so the
// whole family is invalidated by query id rather than by the one key the caller happens to know.
const CREDIT_REGISTRATION_FOR_COURSE_MODULE_QUERY_ID = "getMyCreditRegistrationForCourseModule"

/**
 * Every student-facing view of a registration, for an action that moved the ledger row itself.
 * `courseId` is `null` before the row exists, when no banner can be showing for it either.
 */
export const invalidateRegistrationViews = async (
  queryClient: QueryClient,
  courseId: string | null,
): Promise<void> => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: getMyCreditRegistrationsQueryKey() }),
    courseId
      ? queryClient.invalidateQueries({
          queryKey: getMyCreditRegistrationEnrolmentBannersQueryKey({
            path: { course_id: courseId },
          }),
        })
      : null,
    queryClient.invalidateQueries({
      predicate: (query) =>
        (query.queryKey[0] as { _id?: string } | undefined)?._id ===
        CREDIT_REGISTRATION_FOR_COURSE_MODULE_QUERY_ID,
    }),
  ])
}

/** The one enrolment recheck action, shared by the status page and the in-course banner. */
export const useRequestEnrolmentRecheck = () => {
  const queryClient = useQueryClient()
  return useToastMutation<void, unknown, MyCreditRegistration>(
    async (registration) => {
      await requestCreditRegistrationEnrolmentRecheck({ path: { id: registration.id } })
    },
    { notify: true, method: "POST" },
    {
      onSuccess: async (_result, registration) =>
        await invalidateRegistrationViews(queryClient, registration.course_id),
    },
  )
}

/**
 * Puts the in-course re-enrol banner away for one registration.
 *
 * Not a permanent opt-out: the backend clears the dismissal whenever the registration hits the
 * enrolment problem again, so the banner returns for a new problem.
 */
export const useDismissEnrolmentBanner = () => {
  const queryClient = useQueryClient()
  return useToastMutation<void, unknown, MyCreditRegistration>(
    async (registration) => {
      await dismissCreditRegistrationEnrolmentBanner({ path: { id: registration.id } })
    },
    { notify: false },
    {
      onSuccess: async (_result, registration) =>
        await invalidateRegistrationViews(queryClient, registration.course_id),
    },
  )
}

const useRecordEnrolmentPageVisit = () =>
  useToastMutation<void, unknown, string>(
    async (courseModuleId) => {
      await recordMyEnrolmentPageVisit({ path: { course_module_id: courseModuleId } })
    },
    { notify: false },
  )

/**
 * Tells the backend the student is looking at the enrolment instructions, at most once per mount.
 *
 * The page polls its registration every 10 s; a `useRef` guard, not the polled data, is what keeps
 * a 10-minute visit from firing the request sixty times. Silent on failure: this is a scheduling
 * nudge, not something the student needs to know about.
 */
export const useRecordEnrolmentPageVisitOnce = (
  courseModuleId: string,
  shouldRecord: boolean,
): void => {
  const { mutate: recordVisit } = useRecordEnrolmentPageVisit()
  const hasRecordedRef = useRef(false)
  useEffect(() => {
    if (shouldRecord && !hasRecordedRef.current) {
      hasRecordedRef.current = true
      recordVisit(courseModuleId)
    }
  }, [shouldRecord, courseModuleId, recordVisit])
}
